// Clothing Design Helper - Deep understanding of clothing design
// This file contains comprehensive clothing knowledge for accurate design generation

import {
  getMaterialVariant,
  canonicalizeMaterialVariant,
  getCustomMaterialCategories,
  getAllMaterialVariants
} from './materialHierarchy'
import { appendReferencePhotoToPrompt } from '../constants/referencePhotos'

// Clothing-specific design elements by type
export const CLOTHING_DETAILS = {
  'T-Shirt': {
    fit: ['Regular Fit', 'Slim Fit', 'Oversized', 'Relaxed Fit', 'Athletic Fit'],
    neckline: ['Crew Neck', 'V-Neck', 'Henley', 'Collar', 'Scoop Neck', 'Boat Neck'],
    sleeveLength: ['Short Sleeve', 'Long Sleeve', 'Sleeveless', '3/4 Sleeve', 'Raglan Sleeve'],
    hem: ['Straight Hem', 'Curved Hem', 'Side Slits', 'Asymmetric'],
    details: ['Side Seams', 'Ribbed Collar', 'Double Stitched', 'Tagless', 'Reinforced Shoulders']
  },
  'Shirt': {
    fit: ['Regular Fit', 'Slim Fit', 'Classic Fit', 'Relaxed Fit', 'Tailored Fit'],
    neckline: ['Button-Down Collar', 'Spread Collar', 'Point Collar', 'Band Collar', 'Mandarin Collar'],
    sleeveLength: ['Short Sleeve', 'Long Sleeve', 'Roll-Up Sleeves'],
    cuffs: ['Button Cuffs', 'French Cuffs', 'Barrel Cuffs'],
    placket: ['Standard Placket', 'Hidden Placket', 'Contrast Placket'],
    details: ['Chest Pocket', 'Yoke', 'Side Vents', 'Back Pleat', 'Darting']
  },
  'Jacket': {
    fit: ['Regular Fit', 'Slim Fit', 'Oversized', 'Relaxed Fit', 'Tailored'],
    closure: ['Zipper', 'Buttons', 'Snap Buttons', 'Velcro', 'No Closure'],
    collar: ['Stand Collar', 'Lapel Collar', 'Hood', 'Collarless', 'Funnel Neck'],
    pockets: ['Chest Pockets', 'Side Pockets', 'Internal Pockets', 'Zipper Pockets', 'Patch Pockets'],
    length: ['Crop Length', 'Regular Length', 'Long Length', 'Oversized'],
    details: ['Quilted', 'Lined', 'Reinforced Elbows', 'Adjustable Cuffs', 'Drawstring Waist']
  },
  'Hoodie': {
    fit: ['Regular Fit', 'Slim Fit', 'Oversized', 'Relaxed Fit'],
    hood: ['Standard Hood', 'Adjustable Hood', 'Detachable Hood', 'No Hood'],
    pocket: ['Kangaroo Pocket', 'Side Pockets', 'Zipper Pocket', 'No Pocket'],
    sleeveLength: ['Long Sleeve'],
    cuffs: ['Ribbed Cuffs', 'Elastic Cuffs', 'Open Cuffs'],
    details: ['Drawstring Hood', 'Ribbed Hem', 'Double Stitched', 'Reinforced Seams']
  },
  'Crewneck': {
    fit: ['Regular Fit', 'Slim Fit', 'Oversized', 'Relaxed Fit'],
    sleeveLength: ['Set-in Long Sleeve', 'Raglan Long Sleeve'],
    details: ['Ribbed crew neck', 'Ribbed cuffs', 'Ribbed hem']
  },
  'Sweatpants': {
    fit: ['Regular Fit', 'Slim Fit', 'Relaxed Fit', 'Tapered'],
    hem: ['Cuffed jogger', 'Open hem'],
    details: ['Elastic waist', 'Side pockets', 'Full length legs']
  },
  'Dress': {
    fit: ['Regular Fit', 'Slim Fit', 'A-Line', 'Bodycon', 'Oversized', 'Relaxed'],
    neckline: ['V-Neck', 'Round Neck', 'Square Neck', 'Off-Shoulder', 'Halter', 'Boat Neck', 'Sweetheart'],
    sleeveLength: ['Sleeveless', 'Short Sleeve', 'Long Sleeve', '3/4 Sleeve', 'Cap Sleeve'],
    length: ['Mini', 'Knee Length', 'Midi', 'Maxi', 'Ankle Length'],
    waist: ['Natural Waist', 'Empire Waist', 'Drop Waist', 'No Waist Definition'],
    details: ['Belted', 'Pleated', 'Ruffled', 'Layered', 'Asymmetric Hem']
  },
  'Pants': {
    fit: ['Regular Fit', 'Slim Fit', 'Skinny', 'Relaxed Fit', 'Wide Leg', 'Tapered'],
    waist: ['Low Rise', 'Mid Rise', 'High Rise', 'Elastic Waist', 'Drawstring Waist'],
    length: ['Ankle Length', 'Full Length', 'Cropped', 'Capri'],
    pockets: ['Front Pockets', 'Back Pockets', 'Cargo Pockets', 'Zipper Pockets'],
    details: ['Belt Loops', 'Side Seams', 'Cuffed', 'Distressed', 'Pleated']
  },
  'Jeans': {
    fit: ['Regular Fit', 'Slim Fit', 'Skinny', 'Relaxed Fit', 'Straight Leg', 'Bootcut'],
    waist: ['Low Rise', 'Mid Rise', 'High Rise'],
    wash: ['Dark Wash', 'Medium Wash', 'Light Wash', 'Black', 'White', 'Distressed'],
    details: ['5-Pocket Style', 'Belt Loops', 'Button Fly', 'Zipper Fly', 'Distressed', 'Raw Hem']
  },
  'Sweater': {
    fit: ['Regular Fit', 'Slim Fit', 'Oversized', 'Relaxed Fit'],
    neckline: ['Crew Neck', 'V-Neck', 'Turtleneck', 'Cowl Neck', 'Boat Neck', 'Off-Shoulder'],
    sleeveLength: ['Long Sleeve', 'Short Sleeve', 'Sleeveless', '3/4 Sleeve'],
    knit: ['Cable Knit', 'Ribbed', 'Fair Isle', 'Aran', 'Plain Knit'],
    details: ['Ribbed Cuffs', 'Ribbed Hem', 'Button Details', 'Zipper', 'Pockets']
  }
}

/** New Custom designs can pick these. Other CLOTHING_DETAILS types stay in code. */
export const ENABLED_CUSTOM_GARMENT_TYPES = ['Hoodie', 'Crewneck', 'Sweatpants']

export const SPECIALTY_FABRIC_OPTION = 'Specialty fabric (describe in details)'

/** Hoodie, crewneck, and sweatpants. Older garment types keep the full material list. */
export const SWEAT_GARMENT_MATERIALS = {
  Fleece: ['Cotton fleece', 'Cotton-poly fleece', 'Heavyweight fleece'],
  'French Terry': ['Cotton French terry', 'Cotton-poly French terry'],
  'Cotton Jersey': ['Cotton jersey', 'Organic cotton jersey'],
  Blend: ['Cotton-poly blend', 'Triblend']
}

export function optionsWithCurrent(options, current) {
  const list = Array.isArray(options) ? [...options] : []
  if (current && !list.includes(current)) list.push(current)
  return list
}

export function customGarmentTypeOptions(current) {
  return optionsWithCurrent(ENABLED_CUSTOM_GARMENT_TYPES, current)
}

export function isSpecialtyFabric(material) {
  return material === SPECIALTY_FABRIC_OPTION
}

export function getCustomMaterialsForType(type) {
  if (ENABLED_CUSTOM_GARMENT_TYPES.includes(type)) {
    return [...Object.keys(SWEAT_GARMENT_MATERIALS), SPECIALTY_FABRIC_OPTION]
  }
  return getCustomMaterialCategories()
}

export function getMaterialTypeOptions(type, material) {
  if (!material || isSpecialtyFabric(material)) return []
  if (ENABLED_CUSTOM_GARMENT_TYPES.includes(type) && SWEAT_GARMENT_MATERIALS[material]) {
    return SWEAT_GARMENT_MATERIALS[material]
  }
  return getAllMaterialVariants(material)
}

/** Keeps a specialty request on the quote details field the brief already prints. */
export function quoteSpecificDetails(material, details) {
  const text = typeof details === 'string' ? details.trim() : ''
  if (!isSpecialtyFabric(material)) return text
  if (/specialty fabric/i.test(text)) return text
  return text
    ? `Specialty fabric request: ${text}`
    : 'Specialty fabric request: customer asked for a luxury or specialty fabric.'
}

// Style definitions with detailed descriptions
export const STYLE_GUIDE = {
  'Casual': {
    description: 'Relaxed, comfortable, everyday wear',
    characteristics: ['Comfortable fit', 'Easy to wear', 'Versatile styling', 'Practical details'],
    examples: ['Weekend wear', 'Daily activities', 'Relaxed social settings']
  },
  'Formal': {
    description: 'Elegant, sophisticated, professional appearance',
    characteristics: ['Tailored fit', 'Refined details', 'Quality materials', 'Classic design'],
    examples: ['Business meetings', 'Formal events', 'Professional settings']
  },
  'Vintage': {
    description: 'Classic, retro-inspired, timeless design',
    characteristics: ['Retro patterns', 'Classic silhouettes', 'Nostalgic details', 'Timeless appeal'],
    examples: ['1950s style', '1970s inspired', 'Retro fashion']
  },
  'Contemporary': {
    description: 'Modern, trendy, current fashion',
    characteristics: ['Current trends', 'Modern silhouettes', 'Fashion-forward', 'Edgy details'],
    examples: ['Street style', 'Modern fashion', 'Trendy designs']
  },
  'Minimalist': {
    description: 'Clean, simple, understated elegance',
    characteristics: ['Simple lines', 'Neutral colors', 'Clean design', 'Subtle details'],
    examples: ['Scandinavian style', 'Clean aesthetic', 'Simple elegance']
  },
  'Streetwear': {
    description: 'Urban, edgy, fashion-forward design',
    characteristics: ['Bold graphics', 'Urban aesthetic', 'Comfortable fit', 'Statement pieces'],
    examples: ['Urban fashion', 'Street style', 'Hip-hop inspired']
  },
  'Athletic': {
    description: 'Sporty, functional, performance-oriented',
    characteristics: ['Moisture-wicking', 'Flexible fit', 'Performance materials', 'Athletic details'],
    examples: ['Activewear', 'Sportswear', 'Gym wear']
  },
  'Bohemian': {
    description: 'Free-spirited, artistic, eclectic',
    characteristics: ['Flowing fabrics', 'Artistic patterns', 'Layered details', 'Eclectic mix'],
    examples: ['Boho chic', 'Festival wear', 'Artistic style']
  }
}

// Material properties for better understanding
export const MATERIAL_PROPERTIES = {
  'Cotton': {
    feel: 'Soft, breathable',
    texture: 'Natural texture, slight grain',
    drape: 'Moderate drape',
    bestFor: 'Everyday wear, comfort',
    care: 'Machine washable, may shrink'
  },
  'Wool': {
    feel: 'Warm, slightly coarse',
    texture: 'Rich texture, natural fibers',
    drape: 'Structured drape',
    bestFor: 'Cold weather, warmth',
    care: 'Dry clean or gentle wash'
  },
  'Leather': {
    feel: 'Smooth, durable',
    texture: 'Natural grain, patina',
    drape: 'Structured, holds shape',
    bestFor: 'Jackets, accessories',
    care: 'Leather conditioner, avoid water'
  },
  'Silk': {
    feel: 'Luxurious, smooth',
    texture: 'Smooth, elegant sheen',
    drape: 'Flowing drape',
    bestFor: 'Formal wear, luxury',
    care: 'Dry clean or hand wash'
  },
  'Denim': {
    feel: 'Durable, sturdy',
    texture: 'Textured, classic fade',
    drape: 'Structured',
    bestFor: 'Jeans, casual wear',
    care: 'Machine washable, may fade'
  },
  'Cashmere': {
    feel: 'Ultra-soft, luxurious',
    texture: 'Fine, soft texture',
    drape: 'Elegant drape',
    bestFor: 'Luxury sweaters, warmth',
    care: 'Dry clean or gentle hand wash'
  }
}

// Build comprehensive AI prompt for clothing design
export function buildAdvancedClothingPrompt(specifications, customInstructions = '') {
  const {
    type,
    material,
    materialVariant, // New: specific material variant
    quality,
    style,
    color,
    fit,
    neckline,
    sleeveLength,
    hem,
    additionalNotes,
    presentationMode: rawPres,
    modelGender: rawGenderSpec
  } = specifications

  const presentationMode = rawPres === 'on_model' ? 'on_model' : 'product'
  const g = (rawGenderSpec || 'woman').toString().toLowerCase()
  const modelGender = g === 'man' || g === 'male' ? 'man' : 'woman'
  const modelNoun = modelGender === 'man' ? 'man' : 'woman'

  const garmentName =
    type === 'Crewneck'
      ? 'crewneck sweatshirt'
      : type === 'Sweatpants'
        ? 'pair of sweatpants'
        : type || 'garment'

  // Start with base description
  let prompt = `Create a highly detailed, photorealistic ${garmentName} design. `

  if (type === 'Crewneck') {
    prompt +=
      'This is a crewneck sweatshirt: a pullover with a ribbed crew collar, long sleeves, ribbed cuffs, and a ribbed hem. No hood, no zipper, no buttons, and no v-neck. It is not a t-shirt. '
  }
  if (type === 'Sweatpants') {
    prompt +=
      'These are sweatpants: a pull-on elastic waist, two full-length legs, and side pockets. Not jeans, not shorts, and not a skirt. '
  }

  // Material description with properties - use variant if specified
  if (material) {
    let materialDescription = material.toLowerCase()
    
    // If material variant is specified, use that instead
    if (materialVariant) {
      materialDescription = canonicalizeMaterialVariant(materialVariant).toLowerCase()
      // Add variant-specific details
      const variantInfo = getMaterialVariant(material, materialVariant)
      if (variantInfo) {
        prompt += `Made from ${materialDescription} - ${variantInfo.description}. `
        prompt += `Properties: ${variantInfo.properties}. `
      } else {
        prompt += `Made from ${materialDescription} fabric. `
      }
    } else {
      const materialProps = MATERIAL_PROPERTIES[material] || {}
      prompt += `Made from ${materialDescription} fabric - ${materialProps.feel || 'quality material'}, ${materialProps.texture || 'natural texture'}. `
    }
  }

  // Style description
  if (style) {
    const styleInfo = STYLE_GUIDE[style] || {}
    prompt += `In ${style.toLowerCase()} style: ${styleInfo.description || style.toLowerCase()}. `
    if (styleInfo.characteristics) {
      prompt += `Characteristics include: ${styleInfo.characteristics.join(', ')}. `
    }
  }

  // Color with variations
  if (color) {
    const colorVariations = {
      'Navy Blue': 'deep navy blue, midnight blue, or dark blue',
      'Black': 'rich black, charcoal, or deep black',
      'White': 'pure white, off-white, or cream',
      'Gray': 'charcoal gray, slate gray, or medium gray',
      'Red': 'crimson red, burgundy, or bright red',
      'Blue': 'royal blue, sky blue, or navy blue',
      'Green': 'forest green, emerald green, or olive green',
      'Brown': 'chocolate brown, tan, or camel',
      'Beige': 'beige, tan, or cream',
      'Pink': 'rose pink, blush pink, or coral pink'
    }
    prompt += `Color: ${colorVariations[color] || color.toLowerCase()}. `
  }

  // Fit description
  if (fit) {
    prompt += `Fit: ${fit.toLowerCase()}. `
  }

  // Type-specific details
  if (type && CLOTHING_DETAILS[type]) {
    const details = CLOTHING_DETAILS[type]
    
    if (neckline && details.neckline) {
      prompt += `Neckline: ${neckline.toLowerCase()}. `
    }
    
    if (sleeveLength && details.sleeveLength) {
      prompt += `Sleeve length: ${sleeveLength.toLowerCase()}. `
    }

    if (hem && details.hem) {
      prompt += `Hem: ${hem.toLowerCase()}. `
    }
    
    // Add other type-specific details
    if (details.details && details.details.length > 0) {
      prompt += `Design details: ${details.details.slice(0, 3).join(', ')}. `
    }
  }

  // Quality level
  if (quality) {
    const qualityDescriptions = {
      'budget': 'affordable materials with standard construction, good value',
      'mid-range': 'quality materials with professional construction, excellent value',
      'premium': 'high-end materials with expert craftsmanship, luxury finish',
      'luxury': 'premium materials with handcrafted details, exceptional quality'
    }
    prompt += `Quality level: ${qualityDescriptions[quality] || quality}. `
  }

  // Additional notes
  if (additionalNotes) {
    prompt += `Additional requirements: ${additionalNotes}. `
  }

  // Custom instructions
  if (customInstructions) {
    prompt += `Special design instructions: ${customInstructions}. `
  }

  if (presentationMode === 'on_model') {
    prompt += ` Photorealistic editorial fashion photograph; adult ${modelNoun} model wearing this garment; natural skin texture; professional catalog lighting; high resolution; garment and fabric texture in sharp focus.`
  } else {
    prompt +=
      ' Show the garment in professional fashion photography with clean white background, perfect lighting to highlight fabric texture, construction details, and design elements. Front view, high resolution, with visible stitching, fabric texture, seams, and all design elements clearly defined. No model — product-style shot only. The image should be photorealistic, suitable for fashion catalog, with professional quality.'
  }

  return appendReferencePhotoToPrompt(prompt, specifications)
}

// Get relevant fields for clothing type
export function getRelevantFields(clothingType) {
  if (!clothingType || !CLOTHING_DETAILS[clothingType]) {
    return {
      fit: [],
      neckline: [],
      sleeveLength: [],
      hem: [],
      other: []
    }
  }

  const details = CLOTHING_DETAILS[clothingType]
  const asked = ['fit', 'neckline', 'sleeveLength', 'hem']
  return {
    fit: details.fit || [],
    neckline: details.neckline || [],
    sleeveLength: details.sleeveLength || [],
    hem: details.hem || [],
    other: Object.keys(details).filter(key => !asked.includes(key))
  }
}

// Get style suggestions based on clothing type
export function getStyleSuggestions(clothingType, material) {
  const suggestions = {
    'T-Shirt': {
      'Cotton': ['Casual', 'Streetwear', 'Athletic'],
      'Polyester': ['Athletic', 'Contemporary', 'Streetwear'],
      'Linen': ['Casual', 'Minimalist', 'Bohemian']
    },
    'Jacket': {
      'Leather': ['Contemporary', 'Streetwear', 'Vintage'],
      'Wool': ['Formal', 'Contemporary', 'Vintage'],
      'Denim': ['Casual', 'Streetwear', 'Vintage']
    },
    'Dress': {
      'Silk': ['Formal', 'Contemporary', 'Minimalist'],
      'Cotton': ['Casual', 'Bohemian', 'Contemporary'],
      'Linen': ['Casual', 'Bohemian', 'Minimalist']
    }
  }

  return suggestions[clothingType]?.[material] || ['Casual', 'Contemporary', 'Minimalist']
}

